import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n'; // initialize react-i18next before the app renders
import App from './App';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './AuthContext';
import ErrorBoundary from './Components/ErrorBoundary';

// ── Absorb benign Google reCAPTCHA "Timeout" errors ────────────────────────
// The whole admin app is wrapped in <GoogleReCaptchaProvider>, so Google's
// grecaptcha script keeps a v3 token fresh in the background on EVERY page.
// When a tab has been left idle for a while and the user comes back, that
// background refresh can reject with the bare string "Timeout" (thrown by
// Google's script, not our code). It is harmless — the next explicit
// executeRecaptcha() simply fetches a fresh token — but because it is an
// *unhandled* async rejection it otherwise surfaces as a scary
// "Uncaught runtime errors: Timeout" overlay. We quietly absorb ONLY that
// specific reCAPTCHA-originated timeout (guarded by window.grecaptcha being
// present) and let every other error propagate untouched.
const isBenignRecaptchaTimeout = (reason) => {
  if (typeof window === 'undefined' || !window.grecaptcha) return false;
  const msg =
    typeof reason === 'string'
      ? reason
      : reason && typeof reason.message === 'string'
      ? reason.message
      : '';
  return msg.trim().toLowerCase() === 'timeout';
};

window.addEventListener(
  'unhandledrejection',
  (event) => {
    if (isBenignRecaptchaTimeout(event?.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);

window.addEventListener(
  'error',
  (event) => {
    if (isBenignRecaptchaTimeout(event?.error ?? event?.message)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  true
);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
